import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Grid,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import {
  GetApp,
  PictureAsPdf,
  Code,
  Assessment,
  TrendingUp,
  People,
  BarChart,
  SentimentSatisfied,
  Lightbulb,
  ContentCopy,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { mlAnalysisAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useClients } from '../contexts/ClientContext';
import ClientScopedGate from '../components/ClientScopedGate';
import { useScopedClientSelection } from '../hooks/useScopedClientSelection';
import { useClientScopedGate } from '../hooks/useClientScopedGate';
import {
  ClientSelector,
  MetricCard,
  PageHeader,
} from '../components/primitives';
import {
  ML_ESTIMATE_BASIS,
  MlEstimateBadge,
  MlSectionTitle,
} from '../components/MlEstimateBadge';

const CONTENT_STRATEGY_DISCLAIMER =
  'AI-generated analysis — not sourced from platform data';

const CONTENT_TYPE_ORDER = ['image', 'video', 'text', 'carousel', 'story', 'other'];

const Reports = () => {
  const { showSuccess, showError } = useToast();
  const { clients } = useClients();
  const { selectedClient, clientId, selectClient } = useScopedClientSelection();
  const gate = useClientScopedGate({ clientId, requirePosts: false });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [pageError, setPageError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  useEffect(() => {
    if (!selectedClient || gate.loading || gate.blocked) {
      if (gate.blocked) {
        setReport(null);
        setPageError(null);
      }
      return;
    }
    loadReport(selectedClient.id);
  }, [selectedClient, gate.loading, gate.blocked]);

  const loadReport = async (reportClientId) => {
    setLoading(true);
    setReport(null);
    setPageError(null);
    try {
      const reportData = await mlAnalysisAPI.getClientFullReport(reportClientId);
      setReport(reportData);
    } catch (error) {
      console.error('Error loading report:', error);
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to load report';
      const message =
        typeof detail === 'string' ? detail : 'Failed to load report';
      setPageError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleContentBreakdownBackfill = async () => {
    if (!selectedClient) return;
    setBackfilling(true);
    try {
      await mlAnalysisAPI.triggerContentBreakdownBackfill(selectedClient.id);
      showSuccess(
        'Content breakdown backfill queued. Refresh this report after it finishes.'
      );
    } catch (error) {
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to trigger content breakdown backfill';
      showError(
        typeof detail === 'string'
          ? detail
          : 'Failed to trigger content breakdown backfill'
      );
    } finally {
      setBackfilling(false);
    }
  };

  const hasContentStrategyInsights = Boolean(
    report?.content_strategy_insights &&
      String(report.content_strategy_insights).trim() &&
      !String(report.content_strategy_insights)
        .toLowerCase()
        .includes('require content breakdown')
  );

  const scrapeStrategy = report?.content_strategy || null;
  const hasScrapeStrategy = Boolean(
    scrapeStrategy?.has_scrape_data && (scrapeStrategy.total_posts || 0) > 0
  );
  const contentTypeRollup = scrapeStrategy?.content_type_rollup || {};
  const postingFrequency = scrapeStrategy?.posting_frequency || {};
  const postingPatterns = scrapeStrategy?.posting_patterns || {};
  const dayDistribution = postingPatterns.day_of_week_distribution || {};

  const handleExportPDF = async () => {
    if (!selectedClient || !report) return;

    setExporting(true);
    try {
      // Prefer server-side WeasyPrint PDF (includes Content Strategy + SWOT).
      await mlAnalysisAPI.exportClientReportPDF(selectedClient.id);
      showSuccess('PDF report downloaded successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        'Unknown error';
      const message =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
            : JSON.stringify(detail);
      showError(`Failed to export PDF report: ${message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleExportHTML = async () => {
    if (!selectedClient) return;
    
    setExporting(true);
    try {
      await mlAnalysisAPI.exportClientReportHTML(selectedClient.id);
      showSuccess('HTML report downloaded successfully');
    } catch (error) {
      console.error('Error exporting HTML:', error);
      showError(
        'Failed to export HTML report: ' +
          (error?.response?.data?.detail || error?.message || 'Unknown error'),
      );
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment >= 0.7) return 'success';
    if (sentiment >= 0.4) return 'warning';
    return 'error';
  };

  const getSentimentLabel = (sentiment) => {
    if (sentiment >= 0.7) return 'Positive';
    if (sentiment >= 0.4) return 'Neutral';
    return 'Negative';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Competitive Intelligence Reports"
        subtitle="Generate and export comprehensive competitive analysis reports"
        icon={<Assessment sx={{ fontSize: 28 }} />}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <ClientSelector
          clients={clients}
          value={selectedClient?.id}
          onChange={(client) => client && selectClient(client)}
        />
        {report && !gate.blocked && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPDF}
              disabled={exporting}
              sx={{
                bgcolor: '#dc3545',
                '&:hover': { bgcolor: '#c82333' },
              }}
            >
              {exporting ? 'Exporting...' : 'Export PDF'}
            </Button>
            <Button
              variant="contained"
              startIcon={<Code />}
              onClick={handleExportHTML}
              disabled={exporting}
              sx={{
                bgcolor: '#238636',
                '&:hover': { bgcolor: '#2ea043' },
              }}
            >
              {exporting ? 'Exporting...' : 'Export HTML'}
            </Button>
          </Box>
        )}
      </Box>

      <ClientScopedGate clientId={clientId} minHeight={400} requirePosts={false}>
        {loading && !report ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="320px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
      {!report && selectedClient && !loading && pageError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => selectedClient && loadReport(selectedClient.id)}
            >
              Retry
            </Button>
          }
        >
          Could not generate this competitive report: {pageError}
        </Alert>
      )}

      {!report && selectedClient && !loading && !pageError && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No report data available for this client. Please ensure competitors have been tracked and analyzed.
        </Alert>
      )}

      {report && (
        <>
          {/* Report Header Card */}
          <Card sx={{ mb: 3, bgcolor: '#161b22', border: '1px solid #30363d' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h5" sx={{ color: '#f0f6fc', mb: 1 }}>
                    {report.client_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7d8590' }}>
                    Report Generated: {formatDate(report.report_date)}
                  </Typography>
                </Box>
                <Chip
                  label="Active Report"
                  color="success"
                  sx={{ bgcolor: '#238636', color: '#fff' }}
                />
              </Box>
            </CardContent>
          </Card>

          <Alert severity="info" icon={false} sx={{ mb: 3 }}>
            {ML_ESTIMATE_BASIS.generated_analysis}
          </Alert>

          {/* Tabs */}
          <Paper sx={{ mb: 3, bgcolor: '#161b22', border: '1px solid #30363d' }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                borderBottom: '1px solid #30363d',
                '& .MuiTab-root': {
                  color: '#7d8590',
                  '&.Mui-selected': {
                    color: '#58a6ff',
                  },
                },
              }}
            >
              <Tab icon={<Assessment />} iconPosition="start" label="Executive Summary" />
              <Tab icon={<TrendingUp />} iconPosition="start" label="Competitive Overview" />
              <Tab icon={<BarChart />} iconPosition="start" label="Content Analysis" />
              <Tab icon={<SentimentSatisfied />} iconPosition="start" label="Sentiment" />
              <Tab icon={<Lightbulb />} iconPosition="start" label="Recommendations" />
              <Tab icon={<ContentCopy />} iconPosition="start" label="Content Strategy" />
            </Tabs>
          </Paper>

          {/* Tab Content */}
          <Box sx={{ minHeight: '400px' }}>
            {/* Executive Summary */}
            {activeTab === 0 && report.executive_summary && (
              <Card sx={{ bgcolor: '#161b22', border: '1px solid #30363d', mb: 3 }}>
                <CardContent>
                  <MlSectionTitle basis="generated_analysis" sx={{ color: '#f0f6fc', mb: 3 }}>
                    Executive Summary
                  </MlSectionTitle>
                  
                  {report.executive_summary.narrative && (
                    <Box
                      sx={{
                        bgcolor: '#0d1117',
                        p: 3,
                        borderRadius: 2,
                        mb: 3,
                        borderLeft: '4px solid #58a6ff',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: '#c9d1d9', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}
                      >
                        {report.executive_summary.narrative}
                      </Typography>
                    </Box>
                  )}

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard
                        variant="centered"
                        title="Competitors Tracked"
                        value={report.executive_summary.total_competitors}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard
                        variant="centered"
                        title="Total Followers"
                        value={report.executive_summary.total_followers.toLocaleString()}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard
                        variant="centered"
                        title="Posts Analyzed"
                        value={report.executive_summary.total_posts}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard
                        variant="centered"
                        title="Avg Quality Score"
                        value={report.executive_summary.avg_quality_score.toFixed(2)}
                      />
                    </Grid>
                  </Grid>

                  {report.executive_summary.key_insights && (
                    <Box sx={{ mt: 3 }}>
                      <MlSectionTitle basis="generated_analysis" variant="subtitle1" sx={{ color: '#f0f6fc', mb: 2 }}>
                        Key Insights
                      </MlSectionTitle>
                      {report.executive_summary.key_insights.map((insight, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1,
                            color: '#c9d1d9',
                          }}
                        >
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            • {insight}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Competitive Overview */}
            {activeTab === 1 && report.competitive_overview && (
              <Card sx={{ bgcolor: '#161b22', border: '1px solid #30363d', mb: 3 }}>
                <CardContent>
                  <MlSectionTitle basis="generated_analysis" sx={{ color: '#f0f6fc', mb: 3 }}>
                    Competitive Landscape
                  </MlSectionTitle>
                  
                  {report.competitive_overview.narrative && (
                    <Box
                      sx={{
                        bgcolor: '#0d1117',
                        p: 3,
                        borderRadius: 2,
                        mb: 3,
                        borderLeft: '4px solid #58a6ff',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: '#c9d1d9', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}
                      >
                        {report.competitive_overview.narrative}
                      </Typography>
                    </Box>
                  )}

                  {report.competitive_overview.market_leader && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ color: '#f0f6fc', mb: 1 }}>
                        Market Leader
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#c9d1d9' }}>
                        <strong>{report.competitive_overview.market_leader.name}</strong> leads with{' '}
                        {report.competitive_overview.market_leader.followers.toLocaleString()} followers
                        and an engagement rate of{' '}
                        {(report.competitive_overview.avg_engagement_rate * 100).toFixed(2)}%.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Content Analysis */}
            {activeTab === 2 && report.content_analysis && (
              <Card sx={{ bgcolor: '#161b22', border: '1px solid #30363d', mb: 3 }}>
                <CardContent>
                  <MlSectionTitle basis="quality_rubric" sx={{ color: '#f0f6fc', mb: 3 }}>
                    Content Analysis
                  </MlSectionTitle>
                  
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <Card sx={{ bgcolor: '#0d1117', border: '1px solid #30363d' }}>
                        <CardContent>
                          <Typography variant="h5" sx={{ color: '#58a6ff', mb: 1 }}>
                            {report.content_analysis.avg_engagement.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#7d8590' }}>
                            Average Engagement
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card sx={{ bgcolor: '#0d1117', border: '1px solid #30363d' }}>
                        <CardContent>
                          <Typography variant="h5" sx={{ color: '#58a6ff', mb: 1 }}>
                            {(report.content_analysis.avg_quality_score * 100).toFixed(2)}%
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#7d8590' }}>
                            Average Quality Score
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Sentiment Analysis */}
            {activeTab === 3 && report.sentiment_analysis && (
              <Card sx={{ bgcolor: '#161b22', border: '1px solid #30363d', mb: 3 }}>
                <CardContent>
                  <MlSectionTitle basis="sentiment_nlp" sx={{ color: '#f0f6fc', mb: 3 }}>
                    Sentiment Analysis
                  </MlSectionTitle>
                  
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                      <Card sx={{ bgcolor: '#0d1117', border: '1px solid #30363d' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#28a745', mb: 1 }}>
                            {report.sentiment_analysis.positive_count}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#7d8590' }}>
                            Positive
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card sx={{ bgcolor: '#0d1117', border: '1px solid #30363d' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#ffc107', mb: 1 }}>
                            {report.sentiment_analysis.neutral_count}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#7d8590' }}>
                            Neutral
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card sx={{ bgcolor: '#0d1117', border: '1px solid #30363d' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#dc3545', mb: 1 }}>
                            {report.sentiment_analysis.negative_count}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#7d8590' }}>
                            Negative
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box>
                    <Typography variant="subtitle1" sx={{ color: '#f0f6fc', mb: 1 }}>
                      Overall Sentiment Score
                    </Typography>
                    <Chip
                      label={`${getSentimentLabel(report.sentiment_analysis.avg_sentiment)} (${report.sentiment_analysis.avg_sentiment.toFixed(2)})`}
                      color={getSentimentColor(report.sentiment_analysis.avg_sentiment)}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Recommendations */}
            {activeTab === 4 && report.recommendations && (
              <Card sx={{ bgcolor: '#161b22', border: '1px solid #30363d', mb: 3 }}>
                <CardContent>
                  <MlSectionTitle basis="generated_analysis" sx={{ color: '#f0f6fc', mb: 3 }}>
                    Strategic Recommendations
                  </MlSectionTitle>
                  
                  {report.recommendations.opportunities && report.recommendations.opportunities.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ color: '#28a745', mb: 2 }}>
                        Opportunities
                      </Typography>
                      {report.recommendations.opportunities.map((opp, index) => (
                        <Box
                          key={index}
                          sx={{
                            bgcolor: '#0d1117',
                            p: 2,
                            borderRadius: 1,
                            mb: 2,
                            borderLeft: '4px solid #28a745',
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ color: '#f0f6fc', mb: 1 }}>
                            {opp.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#c9d1d9' }}>
                            {opp.description}
                          </Typography>
                          {opp.impact && (
                            <Typography variant="caption" sx={{ color: '#7d8590', display: 'block', mt: 1 }}>
                              Impact: {opp.impact}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {report.recommendations.threats && report.recommendations.threats.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ color: '#dc3545', mb: 2 }}>
                        Threats
                      </Typography>
                      {report.recommendations.threats.map((threat, index) => (
                        <Box
                          key={index}
                          sx={{
                            bgcolor: '#0d1117',
                            p: 2,
                            borderRadius: 1,
                            mb: 2,
                            borderLeft: '4px solid #dc3545',
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ color: '#f0f6fc', mb: 1 }}>
                            {threat.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#c9d1d9' }}>
                            {threat.description}
                          </Typography>
                          {threat.severity && (
                            <Typography variant="caption" sx={{ color: '#7d8590', display: 'block', mt: 1 }}>
                              Severity: {threat.severity}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {report.recommendations.recommendations && report.recommendations.recommendations.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: '#f0f6fc', mb: 2 }}>
                        Action Items
                      </Typography>
                      {report.recommendations.recommendations.map((rec, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1,
                            color: '#c9d1d9',
                          }}
                        >
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            • {rec}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Content Strategy — Class A scrape panels + optional AI narrative (Phase 17.4) */}
            {activeTab === 5 && (
              <Card sx={{ bgcolor: '#161b22', border: '1px solid #30363d', mb: 3 }}>
                <CardContent>
                  <MlSectionTitle sx={{ color: '#f0f6fc', mb: 1 }}>
                    Content Strategy Insights
                  </MlSectionTitle>
                  <Typography
                    variant="body2"
                    sx={{ color: '#7d8590', mb: 3, lineHeight: 1.6 }}
                  >
                    Built from competitor scrape rows (content type + posted time).
                    Public metrics only — not private platform Insights.
                  </Typography>

                  {hasScrapeStrategy ? (
                    <>
                      {!scrapeStrategy.meets_comparison_gate && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Scraped posts from{' '}
                          {scrapeStrategy.competitors_with_posts || 1} competitor
                          so far. Add a second scraped competitor for a stronger
                          competitive comparison (2+ gate).
                        </Alert>
                      )}

                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                          <MetricCard
                            title="Posts analyzed"
                            value={scrapeStrategy.total_posts || 0}
                            accentColor="#58a6ff"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <MetricCard
                            title="Competitors with posts"
                            value={scrapeStrategy.competitors_with_posts || 0}
                            accentColor="#3fb950"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <MetricCard
                            title="Posts / week"
                            value={
                              postingFrequency.posts_per_week != null
                                ? postingFrequency.posts_per_week
                                : '—'
                            }
                            accentColor="#d29922"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <MetricCard
                            title="Top posting day"
                            value={postingPatterns.top_day_of_week || '—'}
                            accentColor="#a371f7"
                          />
                        </Grid>
                      </Grid>

                      <Typography
                        variant="subtitle1"
                        sx={{ color: '#f0f6fc', mb: 1 }}
                      >
                        Content type breakdown
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: '#7d8590', display: 'block', mb: 1.5 }}
                      >
                        From scrape{' '}
                        <code style={{ fontSize: '0.85em' }}>content_type</code>{' '}
                        on competitor posts (image / video / text).
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          mb: 3,
                        }}
                      >
                        {CONTENT_TYPE_ORDER.filter(
                          (k) => (contentTypeRollup[k] || 0) > 0
                        ).map((key) => (
                          <Chip
                            key={key}
                            label={`${key}: ${contentTypeRollup[key]}`}
                            sx={{
                              bgcolor: '#0d1117',
                              color: '#c9d1d9',
                              border: '1px solid #30363d',
                            }}
                          />
                        ))}
                        {CONTENT_TYPE_ORDER.every(
                          (k) => !(contentTypeRollup[k] > 0)
                        ) && (
                          <Typography variant="body2" sx={{ color: '#7d8590' }}>
                            No content_type values on stored posts yet.
                          </Typography>
                        )}
                      </Box>

                      <Typography
                        variant="subtitle1"
                        sx={{ color: '#f0f6fc', mb: 1 }}
                      >
                        Posting frequency &amp; patterns
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#c9d1d9', mb: 1.5 }}
                      >
                        ~{postingFrequency.posts_per_day ?? '—'} posts/day over{' '}
                        {postingFrequency.window_days ?? '—'} days (
                        {postingFrequency.sample_posts_with_time || 0} posts with
                        posted_time
                        {scrapeStrategy.posts_missing_posted_time
                          ? `; ${scrapeStrategy.posts_missing_posted_time} missing time`
                          : ''}
                        ).
                        {postingPatterns.top_hour != null &&
                          ` Peak hour: ${postingPatterns.top_hour}:00 UTC.`}
                      </Typography>
                      {Object.keys(dayDistribution).length > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            mb: 3,
                          }}
                        >
                          {Object.entries(dayDistribution).map(([day, count]) => (
                            <Chip
                              key={day}
                              size="small"
                              label={`${day}: ${count}`}
                              variant="outlined"
                              sx={{ color: '#8b949e', borderColor: '#30363d' }}
                            />
                          ))}
                        </Box>
                      )}

                      {hasContentStrategyInsights ? (
                        <>
                          <Divider sx={{ borderColor: '#30363d', my: 2 }} />
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1.5,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ color: '#f0f6fc' }}
                            >
                              AI strategy narrative
                            </Typography>
                            <MlEstimateBadge basis="content_insights" />
                          </Box>
                          <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                            {CONTENT_STRATEGY_DISCLAIMER}
                          </Alert>
                          <Box
                            sx={{
                              bgcolor: '#0d1117',
                              p: 3,
                              borderRadius: 2,
                              borderLeft: '4px solid #58a6ff',
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                color: '#c9d1d9',
                                lineHeight: 1.8,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {report.content_strategy_insights}
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Factual scrape panels above are ready. AI narrative
                          needs OpenAI plus pattern enrichment — run content
                          breakdown backfill if topics/captions are empty, then
                          refresh.
                        </Alert>
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          mt: 2,
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={handleContentBreakdownBackfill}
                          disabled={backfilling || !selectedClient}
                        >
                          {backfilling
                            ? 'Queuing…'
                            : 'Run content breakdown backfill'}
                        </Button>
                        <Button
                          variant="text"
                          onClick={() =>
                            selectedClient && loadReport(selectedClient.id)
                          }
                          disabled={loading || !selectedClient}
                        >
                          Refresh report
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Content Strategy needs usable competitor scrapes
                        (Facebook / Instagram / YouTube core) that persist posts
                        with content_type and engagement. Empty scrapes no longer
                        count as fresh (Phase 17.3 MVP). After 2+ competitors have
                        scraped posts, this tab shows type mix and posting
                        patterns automatically.
                      </Alert>
                      <Typography
                        variant="body2"
                        sx={{ color: '#7d8590', mb: 3, lineHeight: 1.7 }}
                      >
                        Scrape competitors first (Operations → Task Monitoring
                        traces scrape → computation → render). Optional backfill
                        enriches captions/topics for the AI narrative. Refresh the
                        report after scrapes complete.
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                        }}
                      >
                        <Button
                          variant="contained"
                          onClick={handleContentBreakdownBackfill}
                          disabled={backfilling || !selectedClient}
                        >
                          {backfilling
                            ? 'Queuing…'
                            : 'Run content breakdown backfill'}
                        </Button>
                        <Button
                          variant="outlined"
                          component={RouterLink}
                          to="/competitors"
                        >
                          Manage competitors
                        </Button>
                        <Button
                          variant="text"
                          component={RouterLink}
                          to="/monitoring"
                        >
                          Task Monitoring
                        </Button>
                        <Button
                          variant="text"
                          onClick={() =>
                            selectedClient && loadReport(selectedClient.id)
                          }
                          disabled={loading || !selectedClient}
                        >
                          Refresh report
                        </Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        </>
      )}
          </>
        )}
      </ClientScopedGate>
    </Container>
  );
};

export default Reports;
